import { observer } from 'mobx-react-lite';
import { clearFiltersButtonText } from 'owa-locstrings/lib/strings/clearfiltersbuttontext.locstring.json';
import { toDateLabel } from 'owa-locstrings/lib/strings/todatelabel.locstring.json';
import { datePlaceholder } from 'owa-locstrings/lib/strings/dateplaceholder.locstring.json';
import { fromDateLabel } from 'owa-locstrings/lib/strings/fromdatelabel.locstring.json';
import { attachmentsLabel as attachmentsLabel_1 } from 'owa-locstrings/lib/strings/attachmentslabel.locstring.json';
import loc, { format } from 'owa-localize';
/* tslint:disable:jsx-no-lambda WI:47601 */
import * as React from 'react';
import getSearchRefinersButtonText from 'owa-calendar-search-store/lib/selectors/getSearchRefinersButtonText';
import {
    selectedCalendarLabel,
    unselectedCalendarLabel,
    calendarOptionAriaLabel,
} from './CalendarSearchRefiners.locstring.json';

import { DropdownDatePicker } from 'owa-datepicker';
import { Callout } from '@fluentui/react/lib/Callout';
import { ControlIcons } from 'owa-control-icons';
import { DirectionalHint } from '@fluentui/react/lib/ContextualMenu';
import { FocusZone } from '@fluentui/react/lib/FocusZone';
import { Icon } from '@fluentui/react/lib/Icon';
import { Label } from '@fluentui/react/lib/Label';
import { Link } from '@fluentui/react/lib/Link';
import { List } from '@fluentui/react/lib/List';
import { Toggle } from '@fluentui/react/lib/Toggle';
import { TooltipHost, TooltipOverflowMode } from '@fluentui/react/lib/Tooltip';
import {
    getCalendarSearchStore,
    RefinerType,
    CalendarFolderSearchScope,
    onClearRefinersButtonClicked,
    setCalendarFoldersScope,
    setFromDate,
    setToDate,
    toggleRefinersVisible,
    onRefinerChanged,
    setIncludeAttachments,
    getFromDate,
    getToDate,
} from 'owa-calendar-search-store';
import type { OwaDate } from 'owa-datetime';
import { FocusTrapZone } from '@fluentui/react/lib/FocusTrapZone';
import { ActionButton } from '@fluentui/react/lib/Button';
import { useId } from '@fluentui/react-hooks';

const classNames = require('classnames');
const styles = require('./CalendarSearchRefiners.scss');

export default observer(function CalendarSearchRefiners(props: {}) {
    const store = getCalendarSearchStore();
    const { includeAttachments, refinersVisible, calendarFoldersScope } = store;
    const toDate = getToDate();
    const fromDate = getFromDate();
    const refinersButtonId = useId('refiners_');
    return (
        <>
            <ActionButton
                id={refinersButtonId}
                className={styles.refinersButton}
                onClick={(evt: any) => {
                    evt.stopPropagation();
                    toggleRefinersVisible();
                }}
                iconProps={{ iconName: ControlIcons.Filter }}>
                <span className={styles.refinersButtonText}>{getSearchRefinersButtonText()}</span>
            </ActionButton>

            {refinersVisible && (
                <div
                    // These event handlers are necessary because of React
                    // portals bubbling events up to the parent.
                    onClick={(evt: React.MouseEvent<unknown>) => evt.stopPropagation()}
                    onKeyDown={(evt: React.KeyboardEvent<HTMLElement>) => evt.stopPropagation()}>
                    <Callout
                        directionalHint={DirectionalHint.bottomRightEdge}
                        directionalHintFixed={true}
                        gapSpace={1}
                        isBeakVisible={false}
                        onDismiss={onCalloutDismiss}
                        target={`#${refinersButtonId}`}>
                        <FocusTrapZone
                            className={styles.refinerContainer}
                            isClickableOutsideFocusTrap={true}>
                            <FocusZone>
                                <List
                                    className={styles.folderOptions}
                                    items={getCalendarScopeOptions(calendarFoldersScope)}
                                    onRenderCell={renderCalendarScopeOption}
                                />
                            </FocusZone>

                            <div className={styles.attachmentsRefinerContainer}>
                                <Label
                                    className={styles.attachmentsLabel}
                                    htmlFor="calendarSearchAttachmentsToggle">
                                    {loc(attachmentsLabel_1)}
                                </Label>
                                <Toggle
                                    id="calendarSearchAttachmentsToggle"
                                    checked={includeAttachments}
                                    className={styles.attachmentsToggle}
                                    onChanged={onAttachmentsToggleToggled}
                                />
                            </div>

                            <div className={styles.dateRefinersContainer}>
                                <div className={styles.dateRefinerContainer}>
                                    <DropdownDatePicker
                                        className={styles.datePicker}
                                        disableAutoFocus={true}
                                        label={loc(fromDateLabel)}
                                        maxDate={toDate}
                                        onSelectDate={onFromDateChanged}
                                        placeholder={loc(datePlaceholder)}
                                        value={fromDate}
                                    />
                                </div>

                                <div className={styles.dateRefinerContainer}>
                                    <DropdownDatePicker
                                        className={styles.datePicker}
                                        disableAutoFocus={true}
                                        label={loc(toDateLabel)}
                                        minDate={fromDate}
                                        onSelectDate={onToDateChanged}
                                        placeholder={loc(datePlaceholder)}
                                        value={toDate}
                                    />
                                </div>
                            </div>

                            <Link
                                className={styles.clearFiltersButton}
                                onClick={onClearFiltersClicked}>
                                {loc(clearFiltersButtonText)}
                            </Link>
                        </FocusTrapZone>
                    </Callout>
                </div>
            )}
        </>
    );
});

// Populate the list of selected calendars for this scope option
function getCalendarScopeOptions(
    searchScopes: CalendarFolderSearchScope[]
): CalendarFolderSearchScope[] {
    const calendarSearchScopes: CalendarFolderSearchScope[] = searchScopes.slice();
    return calendarSearchScopes;
}

// Handler when calendar scope refiner is changed.
function onScopeChanged(calendarScope: CalendarFolderSearchScope) {
    setCalendarFoldersScope(calendarScope);
    onRefinerChanged(RefinerType.calendarScopes);
}

// Handler when from date refiner is changed.
function onFromDateChanged(selectedDate: OwaDate) {
    setFromDate(selectedDate);
    onRefinerChanged(RefinerType.fromDate);
}

// Handler when to date refiner is changed.
function onToDateChanged(selectedDate: OwaDate) {
    setToDate(selectedDate);
    onRefinerChanged(RefinerType.toDate);
}

// Dismiss handler when Fabric Callout thinks it should be dismissed.
function onCalloutDismiss(
    evt: Event | React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement, MouseEvent>
) {
    evt.stopPropagation();
    const { isAutoScroll } = getCalendarSearchStore();
    /**
     * When new search results render, the calendar surface attempts to steal focus,
     * and also autoscroll, so don't allow callout to hide in that scenario.
     */
    if (evt.type !== 'focus' && !isAutoScroll) {
        toggleRefinersVisible();
    }
}

// Handler when "Clear filters" button is pressed.
function onClearFiltersClicked(evt: React.MouseEvent<unknown>) {
    evt.stopPropagation();
    onClearRefinersButtonClicked();
}

// Handler when attachments refiner is changed.
function onAttachmentsToggleToggled(checked: boolean) {
    setIncludeAttachments(checked);
    onRefinerChanged(RefinerType.includeAttachments);
}

// Individual rendering of each selected calendar in the calendar list scope.
function renderCalendarScopeOption(option: CalendarFolderSearchScope, index: number): JSX.Element {
    const isSelected = getCalendarSearchStore().calendarFoldersScope.filter(item => {
        return item.folderId == option.folderId;
    })[0].isSelected;
    return (
        <button
            aria-label={format(
                loc(calendarOptionAriaLabel),
                option.displayName,
                isSelected ? loc(selectedCalendarLabel) : loc(unselectedCalendarLabel)
            )}
            className={classNames(styles.folderOption, !isSelected && styles.isNotSelected)}
            onClick={() => onScopeChanged(option)}
            data-is-focusable={true}>
            {isSelected && (
                <Icon
                    className={styles.selectedFolderCheckMark}
                    iconName={ControlIcons.CheckMark}
                />
            )}
            <span className={styles.calendarDisplayName}>
                <TooltipHost content={option.displayName} overflowMode={TooltipOverflowMode.Parent}>
                    {option.displayName}
                </TooltipHost>
            </span>
        </button>
    );
}
